import { useAtom, useAtomValue } from "jotai";
import { useEffect, useRef, useCallback, useMemo } from "react";
import { throttle } from "lodash";
import { useFinancialApplicationCoreData } from "@/hooks/useFinancialApplicationCoreData";
import { FinancialApplicationSection } from "@/types/enums";
import {
  FinancialApplicationFormSectionContextType,
  FinancialApplicationFormValidationDisplayBehavior,
} from "@/hooks/useFinancialApplicationFormSectionValidation";
import { BasicInformationForm } from "../BasicInformation";
import { KeyPeople } from "../KeyPeople/KeyPeople";
import { OrganizationInfoForm } from "../OrganizationInfo";
import { JointAccountOwners } from "../JointAccountOwners/JointAccountOwners";
import { PersonalAccountBeneficiaries } from "../PersonalAccountBeneficiaries/PersonalAccountBeneficiaries";
import { FinancialProductMultiSelectForm } from "../FinancialProductMultiSelectForm/FinancialProductMultiSelectForm";
import { OptIns } from "../OptIns/OptIns";
// import { LawFirmOrganizationAccountRiskReviewForm } from "../LawFirmOrganizationAccountRiskReviewForm/LawFirmOrganizationAccountRiskReviewForm";
// import { NonLawFirmOrganizationAccountRiskReviewForm } from "../NonLawFirmOrganizationAccountRiskReviewForm/NonLawFirmOrganizationAccountRiskReviewForm";
import { OrganizationAcceptableUseForm } from "../OrganizationAcceptableUse";
import { PersonalAccountAcceptableUseForm } from "../PersonalAccountAcceptableUse/PersonalAccountAcceptableUseForm";
// import { PersonalAccountRiskReviewForm } from "../PersonalAccountRiskReviewForm/PersonalAccountRiskReviewForm";
import { bankerApplicationViewStateAtomFamily } from "@/atoms/bankerApplicationViewStateAtomFamily";
import { AccountDetailsForm } from "../AccountDetails/AccountDetailsForm";
import { APPLICATION_NAVIGATION_ITEMS } from "@/types/ApplicationNavigationItems";
import { FinancialApplicationDocumentsForm } from "../FinancialApplicationDocuments/FinancialApplicationDocumentsForm";

// Helper function to get navigation items as an array when needed
export const getBankerApplicationNavigationItemsArray = () =>
  Object.values(APPLICATION_NAVIGATION_ITEMS);

// Custom hook for all the stateful logic
export const useStickyBankerApplicationDetailsForm = (financialApplicationId: string) => {
  const { conditionalFlags } = useFinancialApplicationCoreData(financialApplicationId);
  const bankerApplicationViewStateAtom = useAtomValue(
    bankerApplicationViewStateAtomFamily(financialApplicationId)
  );
  const [currentSection, setCurrentSection] = useAtom(
    bankerApplicationViewStateAtom.currentSection
  );

  // Refs
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const currentSectionRef = useRef(currentSection);
  const hasInitialScroll = useRef(false);
  // Track the last section that was set by scrolling to distinguish between
  // URL navigation and scroll-triggered navigation
  const lastSectionSetFromScrolling = useRef<string | null>(null);
  // Track when we're programmatically scrolling to prevent interference
  const isProgrammaticallyScrolling = useRef(false);

  // Update currentSectionRef when currentSection changes
  useEffect(() => {
    currentSectionRef.current = currentSection;
  }, [currentSection]);

  // Define navigation items based on available sections
  const navigationItems = useMemo(
    () => [
      {
        id: "product-details",
        label: APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.ProductSelection]?.label,
        section: FinancialApplicationSection.ProductSelection,
        disabled: false,
        component: (
          <FinancialProductMultiSelectForm financialApplicationId={financialApplicationId} />
        ),
      },
      {
        id: "client-details",
        label:
          APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.PersonInformationBasicInfo]
            ?.label,
        section: FinancialApplicationSection.PersonInformationBasicInfo,
        disabled: false,
        component: (
          <BasicInformationForm
            financialApplicationId={financialApplicationId}
            autoValidationBehavior={
              FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
            }
            validationContextType={FinancialApplicationFormSectionContextType.Banker}
            showHeader={true}
            shouldShowDigitalBankingFields={true}
            shouldShowDebitCardFields={true}
          />
        ),
      },
      {
        id: "account-details",
        label: APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.AccountDetails]?.label,
        section: FinancialApplicationSection.AccountDetails,
        disabled: !conditionalFlags.isAccountDetailsEnabled,
        component: (
          <AccountDetailsForm
            financialApplicationId={financialApplicationId}
            autoValidationBehavior={
              FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
            }
            validationContextType={FinancialApplicationFormSectionContextType.Banker}
            showHeader={true}
          />
        ),
      },
      {
        id: "joint-account-owners",
        label: APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.JointAccountOwners]?.label,
        section: FinancialApplicationSection.JointAccountOwners,
        disabled: !conditionalFlags.isJointAccountOwnersEnabled,
        component: (
          <div className="joint-account-owners-info">
            <JointAccountOwners
              financialApplicationId={financialApplicationId}
              initiallyCollapseJointAccountOwnerForms={true}
              validationBehavior={
                FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
              }
              validationContextType={FinancialApplicationFormSectionContextType.Banker}
              shouldAskIfCanProvideGovernmentIdInformation={true}
              shouldAlwaysShowGovernmentIdInformationFields={true}
              shouldShowDigitalBankingFields={true}
              shouldShowDebitCardFields={true}
            />
          </div>
        ),
      },
      {
        id: "personal-account-beneficiaries",
        label:
          APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.PersonalAccountBeneficiaries]
            ?.label,
        section: FinancialApplicationSection.PersonalAccountBeneficiaries,
        disabled: !conditionalFlags.isPersonalAccountBeneficiariesEnabled,
        component: (
          <div className="personal-account-beneficiaries-info">
            <PersonalAccountBeneficiaries
              financialApplicationId={financialApplicationId}
              initiallyCollapsePersonalAccountBeneficiaryForms={true}
              validationBehavior={
                FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
              }
              validationContextType={FinancialApplicationFormSectionContextType.Banker}
            />
          </div>
        ),
      },
      {
        id: "organization-info",
        label:
          APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.OrganizationInformation]?.label,
        section: FinancialApplicationSection.OrganizationInformation,
        disabled: !conditionalFlags.isOrganizationInfoEnabled,
        component: (
          <OrganizationInfoForm
            financialApplicationId={financialApplicationId}
            autoValidationBehavior={
              FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
            }
            validationContextType={FinancialApplicationFormSectionContextType.Banker}
            showHeader={true}
          />
        ),
      },
      {
        id: "key-people",
        label: APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.KeyPeople]?.label,
        section: FinancialApplicationSection.KeyPeople,
        disabled: !conditionalFlags.isKeyPeopleEnabled,
        component: (
          <div className="key-people-info">
            <KeyPeople
              financialApplicationId={financialApplicationId}
              initiallyCollapseKeyPersonForms={true}
              validationBehavior={
                FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
              }
              validationContextType={FinancialApplicationFormSectionContextType.Banker}
              shouldAskIfCanProvideGovernmentIdInformation={true}
              shouldAlwaysShowGovernmentIdInformationFields={true}
              shouldShowDigitalBankingFields={true}
              shouldShowDebitCardFields={true}
            />
          </div>
        ),
      },
      {
        id: "personal-acceptable-use",
        label:
          APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.PersonalAccountAcceptableUse]
            ?.label,
        section: FinancialApplicationSection.PersonalAccountAcceptableUse,
        disabled: !conditionalFlags.isPersonalAccountAcceptableUseEnabled,
        component: (
          <PersonalAccountAcceptableUseForm
            financialApplicationId={financialApplicationId}
            autoValidationBehavior={
              FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
            }
            validationContextType={FinancialApplicationFormSectionContextType.Banker}
          />
        ),
      },
      {
        id: "organization-acceptable-use",
        label:
          APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.OrganizationAccountAcceptableUse]
            ?.label,
        section: FinancialApplicationSection.OrganizationAccountAcceptableUse,
        disabled: !conditionalFlags.isOrganizationAcceptableUseEnabled,
        component: (
          <OrganizationAcceptableUseForm
            financialApplicationId={financialApplicationId}
            autoValidationBehavior={
              FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
            }
            validationContextType={FinancialApplicationFormSectionContextType.Banker}
          />
        ),
      },
      {
        id: "opt-ins",
        label: APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.OptIns]?.label,
        section: FinancialApplicationSection.OptIns,
        disabled: false,
        component: (
          <div className="opt-ins-info">
            <OptIns
              financialApplicationId={financialApplicationId}
              validationBehavior={
                FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
              }
              initiallyCollapsed
              readOnly={false}
              showCdars
              showInsuredCashSweep
              validationContextType={FinancialApplicationFormSectionContextType.Banker}
            />
          </div>
        ),
      },
      {
        id: "financial-application-documents",
        label:
          APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.FinancialApplicationDocuments]
            ?.label,
        section: FinancialApplicationSection.FinancialApplicationDocuments,
        disabled: false,
        component: (
          <FinancialApplicationDocumentsForm
            financialApplicationId={financialApplicationId}
            autoValidationBehavior={
              FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
            }
          />
        ),
      },
      // {
      //   id: "personal-account-risk-review",
      //   label:
      //     APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.PersonalAccountRiskReview]
      //       ?.label,
      //   section: FinancialApplicationSection.PersonalAccountRiskReview,
      //   disabled: !conditionalFlags.isPersonalAccountRiskReviewEnabled,
      //   component: (
      //     <PersonalAccountRiskReviewForm
      //       financialApplicationId={financialApplicationId}
      //       autoValidationBehavior={
      //         FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
      //       }
      //       validationContextType={FinancialApplicationFormSectionContextType.Banker}
      //     />
      //   ),
      // },
      // {
      //   id: "law-firm-organization-account-risk-review",
      //   label:
      //     APPLICATION_NAVIGATION_ITEMS[
      //       FinancialApplicationSection.LawFirmOrganizationAccountRiskReview
      //     ]?.label,
      //   section: FinancialApplicationSection.LawFirmOrganizationAccountRiskReview,
      //   disabled: !conditionalFlags.isLawFirmOrganizationAccountRiskReviewEnabled,
      //   component: (
      //     <LawFirmOrganizationAccountRiskReviewForm
      //       financialApplicationId={financialApplicationId}
      //       autoValidationBehavior={
      //         FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
      //       }
      //       validationContextType={FinancialApplicationFormSectionContextType.Banker}
      //     />
      //   ),
      // },
      // {
      //   id: "non-law-firm-organization-account-risk-review",
      //   label:
      //     APPLICATION_NAVIGATION_ITEMS[
      //       FinancialApplicationSection.NonLawFirmOrganizationAccountRiskReview
      //     ]?.label,
      //   section: FinancialApplicationSection.NonLawFirmOrganizationAccountRiskReview,
      //   disabled: !conditionalFlags.isNonLawFirmOrganizationAccountRiskReviewEnabled,
      //   component: (
      //     <NonLawFirmOrganizationAccountRiskReviewForm
      //       financialApplicationId={financialApplicationId}
      //       autoValidationBehavior={
      //         FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
      //       }
      //       validationContextType={FinancialApplicationFormSectionContextType.Banker}
      //     />
      //   ),
      // },
    ],
    [conditionalFlags, financialApplicationId]
  );

  // Callbacks
  const setSectionRef = useCallback((sectionId: string, element: HTMLDivElement | null) => {
    if (element) {
      sectionRefs.current.set(sectionId, element);
    } else {
      sectionRefs.current.delete(sectionId);
    }
  }, []);

  const scrollToSection = useCallback(
    (sectionId: string) => {
      const element = sectionRefs.current.get(sectionId);
      if (element) {
        // Mark that we're programmatically scrolling
        isProgrammaticallyScrolling.current = true;

        // Scroll to the main section element, not the sticky header
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        setCurrentSection(sectionId);

        // Clear the flag after the scroll animation completes
        setTimeout(() => {
          isProgrammaticallyScrolling.current = false;
        }, 1000); // Give enough time for smooth scroll to complete
      }
    },
    [setCurrentSection]
  );

  // Set up scroll listener to detect sticky headers using the computed style approach
  useEffect(() => {
    const determineStickyState = () => {
      // Skip detection if we're programmatically scrolling
      if (isProgrammaticallyScrolling.current) {
        return;
      }

      // Find which section header is currently in its sticky state
      let stickySection: string | null = null;

      sectionRefs.current.forEach((element, sectionId) => {
        if (element) {
          const stickyElement = element.querySelector(".form-section-header-container");
          if (stickyElement) {
            // Get the computed style to check if it's sticky
            const style = window.getComputedStyle(stickyElement);

            if (style.position === "sticky") {
              // Get the scroll container
              const scrollContainer = stickyElement.closest(".right-content");
              if (scrollContainer) {
                // Get the element's natural position (where it would be without sticky)
                const naturalTop = element.offsetTop;
                const scrollTop = scrollContainer.scrollTop;

                // A sticky element is "stuck" when:
                // 1. The scroll position has passed the element's natural position
                // 2. The element is currently at the top of its scroll container
                const isStuck = scrollTop >= naturalTop;

                if (isStuck) {
                  stickySection = sectionId;
                }
              }
            }
          }
        }
      });

      // Update current section if we found a sticky one and it's different
      if (stickySection && stickySection !== currentSectionRef.current) {
        // Mark that this section change was caused by scrolling
        lastSectionSetFromScrolling.current = stickySection;
        setCurrentSection(stickySection);
      }
    };

    // Reduce throttle delay for more responsive detection
    const throttledDetermineStickyState = throttle(determineStickyState, 100);

    // Add scroll listener to the right-content area
    const rightContent = document.querySelector(".right-content");
    if (rightContent) {
      rightContent.addEventListener("scroll", throttledDetermineStickyState);
    }

    return () => {
      if (rightContent) {
        rightContent.removeEventListener("scroll", throttledDetermineStickyState);
      }
    };
  }, [setCurrentSection]);

  // Handle initial section navigation when page loads
  useEffect(() => {
    // Wait for the page to be stable before attempting to scroll to a section
    const waitForPageStability = () => {
      // Check if all expected sections are available
      const availableSections = Array.from(sectionRefs.current.keys());

      // Wait until all non-disabled sections are available
      const enabledSections = navigationItems
        .filter((item) => !item.disabled)
        .map((item) => item.id);
      const allEnabledSectionsAvailable = enabledSections.every((sectionId) =>
        availableSections.includes(sectionId)
      );

      if (allEnabledSectionsAvailable && availableSections.length > 0) {
        // Page is stable, now scroll to the current section if it's not the first one
        // and we haven't done the initial scroll yet
        if (currentSection !== "product-details" && !hasInitialScroll.current) {
          hasInitialScroll.current = true;
          // Wait a bit more for any final layout calculations
          setTimeout(() => {
            scrollToSection(currentSection);
          }, 500);
        }
      } else {
        // Page not stable yet, wait and try again
        setTimeout(waitForPageStability, 100);
      }
    };

    // Start the stability check after a reasonable delay
    if (!hasInitialScroll.current) {
      const initialDelay = setTimeout(waitForPageStability, 1000);

      return () => clearTimeout(initialDelay);
    } else {
      // Only scroll if this section change was NOT caused by scrolling
      // If the current section is different from what was last set by scrolling,
      // then this change came from URL navigation, so we should scroll to it
      if (lastSectionSetFromScrolling.current !== currentSection) {
        // This is a URL navigation, not scroll-triggered, so scroll to the section
        // Also update the ref to indicate this section was set by navigation
        lastSectionSetFromScrolling.current = currentSection;
        scrollToSection(currentSection);
      }
      // If they're the same, it means scrolling caused this change, so don't scroll again
    }
  }, [navigationItems, scrollToSection, currentSection]);

  return {
    currentSection,
    navigationItems,
    setSectionRef,
    scrollToSection,
  };
};
